/**
 * Logo-aligned chart color palette
 * Blue-teal gradient inspired by brand identity.
 * When org branding overrides are active, getChartColors() reads from CSS variables.
 */

/** Default fallback palette (matches index.css defaults) */
const DEFAULT_CHART_COLORS = [
  "hsl(215, 82%, 50%)", // Logo Blue #186DE9
  "hsl(162, 55%, 40%)", // Logo Teal #2C9C7F
  "hsl(200, 89%, 55%)", // Bright Cyan
  "hsl(198, 62%, 40%)", // Deep Blue-Grey
  "hsl(199, 54%, 67%)", // Soft Sky Blue
  "hsl(180, 65%, 45%)", // Medium Teal
  "hsl(210, 75%, 58%)", // Light Blue
  "hsl(195, 60%, 50%)", // Ocean Blue
  "hsl(170, 50%, 50%)", // Aqua
  "hsl(220, 70%, 55%)", // Periwinkle
];

// Backward-compatible static export
export const chartColors = DEFAULT_CHART_COLORS;

/**
 * Returns the current chart color palette.
 * Reads from CSS custom properties if brand overrides are active,
 * otherwise returns the default palette.
 */
export function getChartColors(): string[] {
  if (typeof window === 'undefined') return DEFAULT_CHART_COLORS;

  const style = getComputedStyle(document.documentElement);
  const chart1 = style.getPropertyValue('--chart-1').trim();

  // If chart-1 matches the default, no branding override is active
  if (!chart1 || chart1 === '215 82% 50%') {
    return DEFAULT_CHART_COLORS;
  }

  // Read the 5 CSS chart colors
  const cssColors = [1, 2, 3, 4, 5].map(i => {
    const val = style.getPropertyValue(`--chart-${i}`).trim();
    return val ? `hsl(${val})` : DEFAULT_CHART_COLORS[i - 1];
  });

  // For colors 6-10, shift hue from primary in 30-degree increments
  const parts = chart1.split(/\s+/).map(v => parseFloat(v));
  const extraColors = [1, 2, 3, 4, 5].map(i => {
    const h = (parts[0] + i * 30) % 360;
    return `hsl(${h}, ${Math.max(parts[1] - 10, 20)}%, ${Math.min(parts[2] + 5, 70)}%)`;
  });

  return [...cssColors, ...extraColors];
}

/**
 * Semantic colors for specific data contexts
 */
export const semanticColors = {
  negative: "hsl(0, 72%, 51%)",    // Error red #EF4444
  warning: "hsl(38, 92%, 50%)",    // Warning orange #F59E0B
  success: "hsl(142, 71%, 45%)",   // Success green #10B981
  neutral: "hsl(240, 16%, 45%)",   // Muted foreground
};

/**
 * Chart styling defaults - blue-tinted
 */
export const chartStyles = {
  gridColor: "#E8F1F8", // Soft blue-tinted grid
  axisColor: "#4A6B82", // Blue-slate axis
  tooltipBg: "#FFFFFF",
  tooltipBorder: "hsl(215, 20%, 88%)", // Blue-gray border
};

/**
 * Get color for chart data by index.
 * Uses dynamic palette when brand overrides are active.
 */
export function getChartColor(index: number): string {
  const colors = getChartColors();
  return colors[index % colors.length];
}
